import { useState, useId } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import { COMPANY_CONFIG } from '../config/site';
import { EnquiryType, ContactFormValues } from '../types';

export function ContactPage() {
  const [searchParams] = useSearchParams();
  const initialSubject = searchParams.get('subject') || '';
  const initialType = (searchParams.get('type') as EnquiryType) || 'General business enquiry';

  const [formValues, setFormValues] = useState<ContactFormValues>({
    fullName: '',
    businessEmail: '',
    company: '',
    enquiryType: initialType,
    message: initialSubject ? `Regarding ${initialSubject}: ` : '',
    privacyConsent: false,
  });

  const [honeypot, setHoneypot] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'server-unavailable' | 'error'>('idle');
  const [submissionMessage, setSubmissionMessage] = useState('');

  const fullNameId = useId();
  const emailId = useId();
  const companyId = useId();
  const enquiryTypeId = useId();
  const messageId = useId();
  const consentId = useId();

  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formValues.fullName.trim()) {
      errors.fullName = 'Please enter your full name.';
    }

    if (!formValues.businessEmail.trim()) {
      errors.businessEmail = 'Please provide a business email address.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formValues.businessEmail)) {
      errors.businessEmail = 'Please enter a valid email address.';
    }

    if (!formValues.message.trim()) {
      errors.message = 'Please provide details about your enquiry.';
    } else if (formValues.message.trim().length < 10) {
      errors.message = 'Message must be at least 10 characters.';
    }

    if (!formValues.privacyConsent) {
      errors.privacyConsent = 'You must acknowledge the privacy notice to submit.';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Honeypot bot protection
    if (honeypot) {
      return;
    }

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Call Cloudflare Pages Function /api/contact
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formValues),
      });

      const data = await response.json().catch(() => null);

      if (response.ok && data?.success) {
        setSubmitStatus('success');
        setSubmissionMessage(
          data.message || 'Thank you for contacting Safaza Technologies. Your inquiry has been received.'
        );
        // Reset form
        setFormValues({
          fullName: '',
          businessEmail: '',
          company: '',
          enquiryType: 'General business enquiry',
          message: '',
          privacyConsent: false,
        });
      } else if (response.status === 501 || data?.code === 'BACKEND_NOT_CONFIGURED') {
        // Transparently inform the user without fake success!
        setSubmitStatus('server-unavailable');
        setSubmissionMessage(
          data?.message ||
            'The contact API endpoint is awaiting production mail delivery configuration. Please email our team directly at contact@safazatech.com.'
        );
      } else {
        setSubmitStatus('error');
        setSubmissionMessage(
          data?.error ||
            'An error occurred transmitting your message. Please reach out directly to contact@safazatech.com.'
        );
      }
    } catch {
      // Network failure or static environment without functions runtime
      setSubmitStatus('server-unavailable');
      setSubmissionMessage(
        'Online submission service is currently offline. Please send your message directly to contact@safazatech.com.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead
        title="Contact Us | Safaza Technologies, Inc."
        description="For business enquiries, product questions, and potential partnerships, please contact Safaza Technologies, Inc."
      />

      {/* Header Banner */}
      <section className="pt-16 pb-16 sm:pt-24 sm:pb-20 bg-white border-b border-[#E4E7EC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0D66E4]/8 border border-[#0D66E4]/20 text-[11px] font-mono font-medium text-[#0D66E4] mb-4">
              <span>GET IN TOUCH</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-[#101828] mb-6">
              Let's start a conversation
              <span className="text-[#0D66E4]">.</span>
            </h1>
            <p className="text-lg text-[#667085] leading-relaxed">
              For custom software development projects, SaaS product inquiries, partnership opportunities,
              or corporate communications, please reach out to Safaza Technologies.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <section className="py-20 sm:py-28 bg-[#F6F8FB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            
            {/* Left Column: Direct Contact Info & Integrity Standards */}
            <div className="lg:col-span-5 space-y-8">
              <div className="p-8 rounded-2xl bg-white border border-[#E4E7EC] shadow-xs space-y-6">
                <h2 className="text-xl font-bold text-[#101828]">
                  Direct Corporate Contact
                </h2>
                <p className="text-sm text-[#667085] leading-relaxed">
                  We welcome inquiries from prospective engineering clients, SaaS customers, technology partners,
                  and corporate correspondents.
                </p>

                <div className="space-y-4 pt-2 border-t border-[#E4E7EC]">
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-[#667085] block mb-1">
                      Verified Electronic Mail
                    </span>
                    <a
                      href={`mailto:${COMPANY_CONFIG.contactEmail}`}
                      className="text-base font-semibold text-[#0D66E4] hover:underline"
                    >
                      {COMPANY_CONFIG.contactEmail}
                    </a>
                  </div>

                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-[#667085] block mb-1">
                      Legal Entity
                    </span>
                    <span className="text-sm font-medium text-[#101828]">
                      {COMPANY_CONFIG.legalName}
                    </span>
                  </div>

                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-[#667085] block mb-1">
                      Incorporation
                    </span>
                    <span className="text-sm text-[#101828]">
                      {COMPANY_CONFIG.incorporationCountry}
                    </span>
                  </div>
                </div>
              </div>

              {/* Scope & Operations Information */}
              <div className="p-6 rounded-2xl bg-white/60 border border-[#E4E7EC] text-xs text-[#667085] space-y-3">
                <h3 className="font-bold text-[#101828]">
                  Commercial Scope &amp; Operations
                </h3>
                <p>
                  Safaza Technologies, Inc. is a US software development company. We develop custom
                  applications, SaaS products, and API integrations for businesses. We do not provide
                  consumer banking, money transmission, payment processing, or currency exchange services.
                </p>
              </div>
            </div>

            {/* Right Column: Contact Form */}
            <div className="lg:col-span-7">
              <div className="double-bezel">
                <div className="double-bezel-inner p-8 sm:p-10">
                  <h2 className="text-2xl font-bold text-[#101828] mb-2">
                    Send an Inquiry
                  </h2>
                  <p className="text-sm text-[#667085] mb-8">
                    Complete the form below and our team will review your message.
                  </p>

                  {/* Submission Status Alerts */}
                  {submitStatus === 'success' && (
                    <div className="mb-8 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm flex items-start gap-3">
                      <svg className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <div>
                        <p className="font-semibold">Message Delivered Successfully</p>
                        <p className="mt-1 text-xs text-emerald-700">{submissionMessage}</p>
                      </div>
                    </div>
                  )}

                  {submitStatus === 'server-unavailable' && (
                    <div className="mb-8 p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm flex items-start gap-3">
                      <svg className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <div>
                        <p className="font-semibold">Notice: Please Email Us Directly</p>
                        <p className="mt-1 text-xs text-amber-800">{submissionMessage}</p>
                        <div className="mt-3">
                          <a
                            href={`mailto:${COMPANY_CONFIG.contactEmail}?subject=${encodeURIComponent(formValues.enquiryType)}&body=${encodeURIComponent(formValues.message)}`}
                            className="inline-flex items-center gap-1.5 text-xs font-bold text-[#0D66E4] hover:underline"
                          >
                            <span>Open email client ({COMPANY_CONFIG.contactEmail})</span>
                            &rarr;
                          </a>
                        </div>
                      </div>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="mb-8 p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-sm flex items-start gap-3">
                      <svg className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <div>
                        <p className="font-semibold">Unable to Send Message</p>
                        <p className="mt-1 text-xs text-rose-700">{submissionMessage}</p>
                      </div>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} noValidate className="space-y-6">
                    {/* Honeypot field for bot protection */}
                    <div className="hidden" aria-hidden="true">
                      <label htmlFor="company_website">Do not fill this field</label>
                      <input
                        type="text"
                        id="company_website"
                        name="company_website"
                        value={honeypot}
                        onChange={(e) => setHoneypot(e.target.value)}
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </div>

                    {/* Full Name */}
                    <div>
                      <label htmlFor={fullNameId} className="block text-xs font-semibold uppercase tracking-wider text-[#101828] mb-2">
                        Full Name <span className="text-[#0D66E4]">*</span>
                      </label>
                      <input
                        id={fullNameId}
                        type="text"
                        value={formValues.fullName}
                        onChange={(e) => setFormValues({ ...formValues, fullName: e.target.value })}
                        className={`w-full px-4 py-3 rounded-xl border text-sm text-[#101828] bg-white transition-colors focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#0D66E4] ${
                          formErrors.fullName ? 'border-rose-400 bg-rose-50/20' : 'border-[#E4E7EC] hover:border-[#98A2B3]'
                        }`}
                        placeholder="e.g. Eleanor Vance"
                        aria-required="true"
                        aria-invalid={Boolean(formErrors.fullName)}
                        aria-describedby={formErrors.fullName ? `${fullNameId}-error` : undefined}
                      />
                      {formErrors.fullName && (
                        <p id={`${fullNameId}-error`} className="mt-1.5 text-xs text-rose-600 font-medium">
                          {formErrors.fullName}
                        </p>
                      )}
                    </div>

                    {/* Business Email & Company Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor={emailId} className="block text-xs font-semibold uppercase tracking-wider text-[#101828] mb-2">
                          Business Email <span className="text-[#0D66E4]">*</span>
                        </label>
                        <input
                          id={emailId}
                          type="email"
                          value={formValues.businessEmail}
                          onChange={(e) => setFormValues({ ...formValues, businessEmail: e.target.value })}
                          className={`w-full px-4 py-3 rounded-xl border text-sm text-[#101828] bg-white transition-colors focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#0D66E4] ${
                            formErrors.businessEmail ? 'border-rose-400 bg-rose-50/20' : 'border-[#E4E7EC] hover:border-[#98A2B3]'
                          }`}
                          placeholder="name@company.com"
                          aria-required="true"
                          aria-invalid={Boolean(formErrors.businessEmail)}
                          aria-describedby={formErrors.businessEmail ? `${emailId}-error` : undefined}
                        />
                        {formErrors.businessEmail && (
                          <p id={`${emailId}-error`} className="mt-1.5 text-xs text-rose-600 font-medium">
                            {formErrors.businessEmail}
                          </p>
                        )}
                      </div>

                      <div>
                        <label htmlFor={companyId} className="block text-xs font-semibold uppercase tracking-wider text-[#101828] mb-2">
                          Company / Organization <span className="text-[#98A2B3] lowercase font-normal">(optional)</span>
                        </label>
                        <input
                          id={companyId}
                          type="text"
                          value={formValues.company}
                          onChange={(e) => setFormValues({ ...formValues, company: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl border border-[#E4E7EC] hover:border-[#98A2B3] text-sm text-[#101828] bg-white transition-colors focus:outline-none focus:ring-2 focus:ring-[#0D66E4]"
                          placeholder="Organization name"
                        />
                      </div>
                    </div>

                    {/* Enquiry Type Selection */}
                    <div>
                      <label htmlFor={enquiryTypeId} className="block text-xs font-semibold uppercase tracking-wider text-[#101828] mb-2">
                        Enquiry Type <span className="text-[#0D66E4]">*</span>
                      </label>
                      <select
                        id={enquiryTypeId}
                        value={formValues.enquiryType}
                        onChange={(e) => setFormValues({ ...formValues, enquiryType: e.target.value as EnquiryType })}
                        className="w-full px-4 py-3 rounded-xl border border-[#E4E7EC] text-sm text-[#101828] bg-white focus:outline-none focus:ring-2 focus:ring-[#0D66E4]"
                      >
                        <option value="Custom software development">Custom software development</option>
                        <option value="Fintech software engineering">Fintech software engineering</option>
                        <option value="SaaS & product inquiry">SaaS &amp; product inquiry</option>
                        <option value="Partnership inquiry">Partnership inquiry</option>
                        <option value="General business enquiry">General business enquiry</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    {/* Message Area */}
                    <div>
                      <label htmlFor={messageId} className="block text-xs font-semibold uppercase tracking-wider text-[#101828] mb-2">
                        Message <span className="text-[#0D66E4]">*</span>
                      </label>
                      <textarea
                        id={messageId}
                        rows={5}
                        value={formValues.message}
                        onChange={(e) => setFormValues({ ...formValues, message: e.target.value })}
                        className={`w-full px-4 py-3 rounded-xl border text-sm text-[#101828] bg-white transition-colors focus:outline-none focus:ring-2 focus:ring-[#0D66E4] ${
                          formErrors.message ? 'border-rose-400 bg-rose-50/20' : 'border-[#E4E7EC] hover:border-[#98A2B3]'
                        }`}
                        placeholder="Please describe the purpose of your enquiry or commercial discussion..."
                        aria-required="true"
                        aria-invalid={Boolean(formErrors.message)}
                        aria-describedby={formErrors.message ? `${messageId}-error` : undefined}
                      />
                      {formErrors.message && (
                        <p id={`${messageId}-error`} className="mt-1.5 text-xs text-rose-600 font-medium">
                          {formErrors.message}
                        </p>
                      )}
                    </div>

                    {/* Privacy Notice & Consent */}
                    <div>
                      <div className="flex items-start gap-3">
                        <input
                          id={consentId}
                          type="checkbox"
                          checked={formValues.privacyConsent}
                          onChange={(e) => setFormValues({ ...formValues, privacyConsent: e.target.checked })}
                          className="mt-1 h-4 w-4 rounded border-gray-300 text-[#0D66E4] focus:ring-[#0D66E4]"
                          aria-required="true"
                        />
                        <label htmlFor={consentId} className="text-xs text-[#667085] leading-relaxed">
                          I agree that Safaza Technologies, Inc. may process the submitted contact details to respond to this business inquiry in accordance with our{' '}
                          <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#0D66E4] underline">
                            Privacy Policy
                          </a>
                          .
                        </label>
                      </div>
                      {formErrors.privacyConsent && (
                        <p className="mt-1.5 text-xs text-rose-600 font-medium">
                          {formErrors.privacyConsent}
                        </p>
                      )}
                    </div>

                    {/* Submit Button */}
                    <div>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-3.5 text-sm font-semibold text-white bg-[#101828] hover:bg-[#0D66E4] active:scale-[0.98] rounded-full transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-xs hover:shadow-md"
                      >
                        {isSubmitting ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            <span>Submitting message...</span>
                          </>
                        ) : (
                          <>
                            <span>Send inquiry</span>
                            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                            </svg>
                          </>
                        )}
                      </button>
                    </div>

                  </form>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </>
  );
}
